import { useCallback, useMemo } from "react";
import { Modal } from "shared/components/Modal";
import {
  IconButton,
  IconButtonSize,
  IconButtonVariant
} from "shared/components/IconButton";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { instance } from "api/apiBase";
import { useAuthContext } from "providers/Auth";

import { Cancel01Icon } from "@hugeicons-pro/core-stroke-rounded";
import {
  CreateLeadPayload,
  useFormController
} from "shared/app/LeadsTableView/components/CreateLeadModal/useFormController";
import { BasicInfoSection } from "shared/app/LeadsTableView/components/CreateLeadModal/sections/BasicInfo";
import { ContactInfoSection } from "shared/app/LeadsTableView/components/CreateLeadModal/sections/ContactInfo";
import { InterestedInSection } from "shared/app/LeadsTableView/components/CreateLeadModal/sections/InterestedIn";
import { ProfileSection } from "shared/app/LeadsTableView/components/CreateLeadModal/sections/Profile";
import { DetailsSection } from "shared/app/LeadsTableView/components/CreateLeadModal/sections/Details";
import { Footer } from "shared/app/LeadsTableView/components/CreateLeadModal/Footer";
import { useDuplicateCheck } from "shared/app/LeadsTableView/components/CreateLeadModal/hooks/useDuplicateCheck";
import { getLeadMutationErrorMessage } from "shared/app/LeadsTableView/utils/leadErrors";
import { LeadType } from "views/leads-id/types";

export interface CreateLeadDefaults {
  property?: { id: number; name: string };
  unit?: { id: number; title: string };
  // 10-digit phone (no country code) to pre-populate the primary phone field.
  primaryPhone?: string;
  // When converting an AI call transcript, links the new lead to that transcript
  // server-side in the create request (avoids a separate, orphan-prone PATCH).
  callTranscriptId?: number;
}

export const CreateLeadModal = ({
  open,
  onClose,
  refetchLeads,
  defaults,
  onCreated,
  zIndex
}: {
  open: boolean;
  onClose: () => void;
  refetchLeads: () => void;
  defaults?: CreateLeadDefaults;
  // Fires with the created lead after a successful save. Optional so existing
  // callers are unaffected.
  onCreated?: (lead: { id: number }) => void;
  // Lets callers raise the modal above higher-stacking surfaces (e.g. a drawer).
  zIndex?: number;
}) => {
  const initialValues = useMemo<Partial<CreateLeadPayload> | undefined>(() => {
    if (!defaults?.property && !defaults?.unit && !defaults?.primaryPhone)
      return undefined;
    return {
      property_id: defaults.property?.id,
      unit_id: defaults.unit?.id,
      primary_phone: defaults.primaryPhone
    };
  }, [defaults]);

  const initialLabels = useMemo(
    () => ({
      property: defaults?.property?.name,
      unit: defaults?.unit?.title
    }),
    [defaults]
  );

  const { type } = useAuthContext();
  // Owner leads can't be tied to a property/unit, so on the Community/Unit page
  // leads tabs (which pre-fill a property/unit) only tenant leads make sense —
  // hide the Type select there and keep the default Tenant type.
  const hasEntityContext = Boolean(defaults?.property || defaults?.unit);
  const isOwnerLeadsEnabled = type.isOwnerLeads && !hasEntityContext;
  const isTenantLeadsEnabled = type.isTenantLeads;

  const form = useFormController({
    initialValues:
      !isTenantLeadsEnabled && isOwnerLeadsEnabled
        ? { ...initialValues, lead_type: LeadType.Owner }
        : initialValues
  });
  const isOwner = form.values.lead_type === LeadType.Owner;
  const {
    duplicates,
    check: checkDuplicates,
    reset: resetDuplicates
  } = useDuplicateCheck();

  const handleContactFieldBlur = useCallback(() => {
    const allEmails = [
      form.values.primary_email ?? "",
      ...(form.values.other_emails ?? [])
    ];
    const allPhones = [
      form.values.primary_phone ?? "",
      ...(form.values.other_phones ?? [])
    ];
    checkDuplicates(allEmails, allPhones);
  }, [
    form.values.primary_email,
    form.values.primary_phone,
    form.values.other_emails,
    form.values.other_phones,
    checkDuplicates
  ]);

  const handleClose = () => {
    onClose();
    form.reset();
    resetDuplicates();
  };

  const { mutate: createLead, isPending } = useMutation({
    mutationKey: ["CREATE_LEAD"],
    mutationFn: async (values: CreateLeadPayload) => {
      // Build contact methods; skip empty primaries, add "1" prefix to phones.
      const processedContactMethods = [
        ...(values.primary_phone?.trim()
          ? [
              {
                type: "phone",
                value: `1${values.primary_phone}`,
                is_primary: true
              }
            ]
          : []),
        ...(values.primary_email?.trim()
          ? [
              {
                type: "email",
                value: values.primary_email,
                is_primary: true
              }
            ]
          : []),
        ...values.other_phones
          .filter((phone) => phone.trim() !== "")
          .map((phone) => ({
            type: "phone",
            value: `1${phone}`,
            is_primary: false
          })),
        ...values.other_emails
          .filter((email) => email.trim() !== "")
          .map((email) => ({ type: "email", value: email, is_primary: false }))
      ];

      const {
        property_id,
        unit_id,
        desired_move_in_date,
        message,
        portfolio_size,
        current_property_manager,
        ...rest
      } = values;

      // Owner leads can't be linked to a property/unit and have no move-in or
      // message field; tenant leads don't carry portfolio fields. Send only the
      // fields that apply to the selected type so the backend never rejects it.
      const typeSpecificFields =
        values.lead_type === LeadType.Owner
          ? {
              portfolio_size: portfolio_size ?? null,
              current_property_manager: current_property_manager || null
            }
          : { property_id, unit_id, desired_move_in_date, message };

      return instance
        .post<{ id: number }>("/portal/v1/leads", {
          ...rest,
          ...typeSpecificFields,
          contact_methods: processedContactMethods,
          call_transcript_id: defaults?.callTranscriptId,
          source_id: Number(values.source_id) || undefined,
          assignee_ids:
            values.assignee_id && Number(values.assignee_id)
              ? [Number(values.assignee_id)]
              : undefined
        })
        .then((res) => res.data);
    },
    onSuccess: (lead) => {
      refetchLeads();
      onCreated?.(lead);
      handleClose();
    },
    onError: (error) =>
      toast.error(getLeadMutationErrorMessage(error, { isOwnerLead: isOwner }))
  });

  return (
    <Modal
      opened={open}
      onClose={handleClose}
      zIndex={zIndex}
      panelClassName="w-full max-w-[816px] p-0 overflow-y-hidden mx-5"
    >
      <form
        onSubmit={form.onSubmit({
          onSuccess: () => createLead(form.values as CreateLeadPayload)
        })}
        className="flex flex-col max-h-[85vh]"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-alpha-100 bg-white sticky top-0 z-10">
          <div>
            <h3 className="text-xl leading-6 font-bold text-content-1000">
              Create lead
            </h3>
          </div>
          <IconButton
            aria-label="Close"
            variant={IconButtonVariant.TEXT_BASE}
            size={IconButtonSize.SM}
            HugeIcon={Cancel01Icon}
            onClick={handleClose}
          />
        </div>
        {/* Body (scroll area) */}
        <div className="flex-1 overflow-y-auto p-6 flex flex-col gap-6 bg-background-900">
          <BasicInfoSection
            register={form.register}
            showTypeSelect={isOwnerLeadsEnabled && isTenantLeadsEnabled}
            leadType={form.values.lead_type}
            onLeadTypeChange={(value) =>
              form.handleChange("lead_type", () => value)()
            }
          />
          <ContactInfoSection
            form={form}
            duplicates={duplicates}
            onContactFieldBlur={handleContactFieldBlur}
          />
          {isOwner ? (
            <ProfileSection form={form} />
          ) : (
            <InterestedInSection form={form} initialLabels={initialLabels} />
          )}
          <DetailsSection
            form={form}
            isOwner={isOwner}
            propertyId={defaults?.property?.id}
          />
        </div>
        <Footer onClose={handleClose} isPending={isPending} />
      </form>
    </Modal>
  );
};
